/* eslint-disable no-empty */
import React, { useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material UI
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';

// Material Icons
import SearchIcon from '@material-ui/icons/Search';

// useDebounce
import { useDebounce } from 'use-debounce';

// Helpers
import { isDate } from 'helpers/date';

// Actions
import { setIsSearchActive } from 'store/ducks/reports/actions';

// Types
import { ApplicationState } from 'store';

// Constants
import IMAGE from 'constants/images';

// Components
import Badge from 'components/Badge';

// Styles
import useStyles from './SearchComponent.styles';

interface SearchComponentInterface {
  data?: Array<any>;
  itemsByFilter?: Array<{
    key: string;
    value: string;
  }>;
  inputSize?: 'small' | 'medium';
  classes?: {
    root?: string;
    container?: string;
  };
  results?: any;
  disabledInput?: boolean;
  placeholder?: string;
  modalFilter?: () => void;
  currNavGuide?: number;
  mw?: string;
}

type Props = SearchComponentInterface;

const SearchComponent: React.FC<Props> = ({
  data,
  itemsByFilter,
  inputSize,
  classes,
  results,
  disabledInput,
  placeholder,
  modalFilter,
  currNavGuide,
  mw,
}) => {
  const Classes = useStyles();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');

  const [debounceSearchTerm] = useDebounce(searchTerm, 1000);

  const filterItems = useSelector(
    (state: ApplicationState) => state.reports.filterItems,
  );
  const isSearchActive = useSelector(
    (state: ApplicationState) => state.reports.isSearchActive,
  );

  useEffect(() => {
    if (!Number.isNaN(currNavGuide)) setSearchTerm('');
  }, [currNavGuide]);

  useEffect(() => {
    if (!results) return;

    if (debounceSearchTerm) {
      if (!itemsByFilter?.length) return;
      dispatch(setIsSearchActive(true));

      const allowNumbersAndLettersOnly = /^[a-zA-Z0-9.,/]+$/;
      const removeDuplicates = (array: any[]) => Array.from(new Set(array));

      if (!allowNumbersAndLettersOnly.test(searchTerm.replace(' ', ''))) return;

      const updatedList = itemsByFilter
        .map((item) => item)
        .map(({ key }) => key)
        .map(
          (w) =>
            Array.isArray(data) &&
            data.filter((item: any) => {
              if (item?.[w]) {
                try {
                  if (typeof item[w] === 'number') {
                    return String(item?.[w]).includes(
                      searchTerm.replace(',', '.'),
                    );
                  }
                  // if date, format than
                  if (isDate(item[w])) {
                    return new Date(item?.[w])
                      .toLocaleDateString('pt-BR')
                      .includes(searchTerm?.trim());
                  }

                  return item[w]
                    ?.toLowerCase()
                    ?.trim()
                    ?.includes(searchTerm?.trim()?.toLowerCase());
                } catch (error) {}
              }
              return '';
            }),
        )
        .flatMap((listItems: any) => listItems);

      results(removeDuplicates(updatedList));

      if (!updatedList.length) {
        results([]);
      }
    } else {
      results(data);
      dispatch(setIsSearchActive(false));
    }
    // eslint-disable-next-line
  }, [debounceSearchTerm]);

  return (
    <>
      <Grid item lg={6} md={12} sm={12} xl={6} xs={12}>
        <Grid
          alignItems='center'
          container
          justifyContent='flex-start'
          spacing={1}
          className={Classes.minWidth}>
          <Grid item className={Classes.flex}>
            <FormControl className={mw || ''}>
              <TextField
                disabled={disabledInput}
                size={inputSize || 'medium'}
                classes={{
                  root: [Classes.input, classes?.root].join(' '),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {modalFilter instanceof Function && (
                        <Box position='relative'>
                          <IconButton
                            // disabled={disabledInput}
                            onClick={modalFilter}>
                            <img src={IMAGE.ICONS.FILTORS} alt='filters' />
                          </IconButton>
                          {filterItems?.length > 0 ? (
                            <Badge value={filterItems.length} />
                          ) : null}
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
                placeholder={placeholder || 'Buscar'}
                variant='outlined'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchComponent;
export { SearchComponent };
